import { mapGetters, mapActions, mapMutations } from 'vuex'
import ChatGPT from '@/components/ChatGPT/ChatGPT.vue'

const getChipColor = (article) => {
  if (article.article_url || article.status === 'published') return 'green lighten-4'
  if (article.status !== 'no status') return 'blue lighten-4'
  return 'grey lighten-4'
}

const getDisplayTitle = (article) => {
  if (!article) return ''
  return article.title === '-' ? article.article_url : article.title
}

export default {
  name: 'Favorites',
  components: { ChatGPT },
  props: {
    sharedMode: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      showCopilot: false,
      searchText: '',
      statusFilter: 'all',
      funnels: ['top', 'middle', 'bottom'],
      editMode: false,
      draggedArticle: null,
      currentDropTarget: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      statusOptions: [
        { text: 'All statuses', value: 'all' },
        { text: 'Published', value: 'published' },
        { text: 'In progress', value: 'inProgress' },
        { text: 'New ideas', value: 'newIdea' }
      ]
    }
  },
  computed: {
    ...mapGetters(['topics', 'articles', 'activeContentplan', 'activeWorkspace']),
    workspaceId () { return parseInt(this.$route.params.workspaceId) },
    contentplanId () { return parseInt(this.$route.params.contentplanId) },
    contentplanName () { return this.activeContentplan?.name ?? '' },
    remainingCredits () { return this.activeWorkspace?.remaining_article_credits },
    hasUntopicedArticles () {
      return this.articles?.some(a => a.show_in_map && (a.topic_id === null || a.topic_id === undefined))
    },
    displayTopics () {
      const currentTopics = Array.isArray(this.topics) ? [...this.topics] : []
      if (this.hasUntopicedArticles) {
        currentTopics.push({ id: 'no-hub', name: 'No Hub', isTemporary: true })
      }
      return currentTopics
    },
    mapArticles () {
      if (!this.articles?.length) return []
      return this.articles.filter(a => a.show_in_map)
    },
    filteredArticles () {
      let result = this.mapArticles
      if (this.searchText) {
        const search = this.searchText.toLowerCase()
        result = result.filter(a => {
          const title = getDisplayTitle(a)
          return title && title.toLowerCase().includes(search)
        })
      }
      if (this.statusFilter && this.statusFilter !== 'all') {
        result = result.filter(a => {
          if (this.statusFilter === 'published') return a.article_url || a.status === 'published'
          if (this.statusFilter === 'inProgress') return !a.article_url && a.status !== 'published' && a.status !== 'no status'
          if (this.statusFilter === 'newIdea') return !a.article_url && a.status !== 'published' && a.status === 'no status'
          return true
        })
      }
      return result
    },
    statusCounts () {
      const articles = this.mapArticles
      return {
        published: articles.filter(a => a.article_url || a.status === 'published').length,
        inProgress: articles.filter(a => !a.article_url && a.status !== 'published' && a.status !== 'no status').length,
        newIdea: articles.filter(a => !a.article_url && a.status !== 'published' && a.status === 'no status').length
      }
    }
  },
  watch: {
    async $route () {
      await this.loadData()
    }
  },
  methods: {
    getChipColor,
    getDisplayTitle,
    ...mapActions(['articleGet', 'topicGetList', 'articlePut', 'topicPut']),
    ...mapMutations(['setArticles']),

    async loadData () {
      try {
        this.isLoading = true
        if (!this.sharedMode) {
          document.title = 'Favorites | ContentGecko'
          await Promise.all([
            this.articleGet({
              contentplanId: this.contentplanId,
              filters: [{ field: 'showInMap', op: 'eq', value: 1 }],
              pagination: { limit: 500, offset: 0 },
              commitType: 'basic'
            }),
            this.topicGetList({ contentplanId: this.contentplanId })
          ])
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },

    getCellArticles (topic, funnel) {
      const topicId = topic.id
      return this.filteredArticles.filter(a => {
        if (a.type === 'product' || a.type === 'category') return false
        if (topicId === 'no-hub') return !a.topic_id && a.funnel === funnel
        return a.topic_id === topicId && a.funnel === funnel
      })
    },

    getTopicArticleCount (topic) {
      if (!this.filteredArticles?.length) return '0'
      const topicId = topic.id
      return String(this.filteredArticles.filter(a => {
        if (a.type === 'product' || a.type === 'category') return false
        if (topicId === 'no-hub') return !a.topic_id
        return a.topic_id === topicId
      }).length)
    },

    articleRoute (article) {
      if (this.editMode || this.sharedMode) return null
      return {
        name: 'Article',
        params: {
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          articleId: article.article_id
        }
      }
    },

    showFeedback (message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.showSnackbar = true
    },

    handleDragStart (event, article) {
      this.draggedArticle = article
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', JSON.stringify({
        articleId: article.article_id,
        currentTopic: article.topic_id,
        currentFunnel: article.funnel,
        articleType: article.type
      }))
      event.target.classList.add('dragging')
    },

    handleDragEnd () {
      this.draggedArticle = null
      this.currentDropTarget = null
      document.querySelectorAll('.dragging').forEach(el => el.classList.remove('dragging'))
      document.querySelectorAll('.active-drop-target').forEach(el => el.classList.remove('active-drop-target'))
    },

    handleDragOver (event, topicId, funnel) {
      if (!this.draggedArticle) return
      const isProduct = this.draggedArticle.type === 'product' || this.draggedArticle.type === 'category'
      const allowDrop = isProduct ? funnel === 'product' : funnel !== 'product'

      if (allowDrop) {
        event.dataTransfer.dropEffect = 'move'
        if (!this.currentDropTarget || this.currentDropTarget.topicId !== topicId || this.currentDropTarget.funnel !== funnel) {
          this.currentDropTarget = { topicId, funnel }
        }
      } else {
        event.dataTransfer.dropEffect = 'none'
      }
    },

    handleDragEnter (event, topicId, funnel) {
      if (!this.draggedArticle) return
      this.currentDropTarget = { topicId, funnel }
      const isProduct = this.draggedArticle.type === 'product' || this.draggedArticle.type === 'category'
      const isValid = isProduct ? funnel === 'product' : funnel !== 'product'
      if (isValid) event.currentTarget.classList.add('active-drop-target')
      else event.currentTarget.classList.remove('active-drop-target')
    },

    handleDragLeave (event) {
      event.currentTarget.classList.remove('active-drop-target')
    },

    async handleDrop (event, targetTopicId, targetFunnel) {
      let data
      try {
        data = JSON.parse(event.dataTransfer.getData('text/plain'))
      } catch (e) {
        this.draggedArticle = null
        return
      }

      const { articleId, currentTopic, currentFunnel, articleType } = data
      event.currentTarget.classList.remove('active-drop-target')

      if (articleType === 'product' || articleType === 'category') {
        if (targetFunnel !== 'product') {
          this.showFeedback('Products can only be moved to Product rows.', 'error')
          this.draggedArticle = null
          return
        }
        if (currentTopic === targetTopicId) {
          this.draggedArticle = null
          return
        }
      } else {
        if (currentTopic === targetTopicId && currentFunnel === targetFunnel) {
          this.draggedArticle = null
          return
        }
        if (targetFunnel === 'product') {
          this.showFeedback('Cannot move non-product articles to Product rows.', 'error')
          this.draggedArticle = null
          return
        }
      }

      try {
        const article = this.articles.find(a => a.article_id === articleId)
        if (!article) { this.draggedArticle = null; return }

        const updatedArticles = this.articles.map(a => {
          if (a.article_id !== articleId) return a
          return {
            ...a,
            topic_id: targetTopicId === 'no-hub' ? null : targetTopicId,
            funnel: (articleType === 'product' || articleType === 'category') ? a.funnel : targetFunnel
          }
        })
        this.setArticles(updatedArticles)

        const changes = []
        if (currentTopic !== targetTopicId) {
          changes.push(this.articlePut({
            contentplanId: this.contentplanId,
            action: 'updateTopic',
            articleIds: [articleId],
            topicId: targetTopicId === 'no-hub' ? null : targetTopicId
          }))
        }
        if ((articleType !== 'product' && articleType !== 'category') && currentFunnel !== targetFunnel) {
          changes.push(this.articlePut({
            contentplanId: this.contentplanId,
            action: 'updateFunnel',
            articleIds: [articleId],
            funnel: targetFunnel
          }))
        }

        if (changes.length > 0) {
          this.showFeedback('Article moved', 'success')
          Promise.all(changes)
            .catch(() => {
              this.showFeedback('Failed to save — refreshing', 'error')
              this.loadData()
            })
        }
      } catch (error) {
        this.showFeedback('An error occurred — refreshing', 'error')
        this.loadData()
      } finally {
        this.draggedArticle = null
      }
    },

    isDraggingArticle (article) {
      return this.draggedArticle && this.draggedArticle.article_id === article.article_id
    },

    async removeItem (article) {
      try {
        await this.articlePut({
          contentplanId: this.contentplanId,
          action: 'removeFromMap',
          articleIds: [article.article_id]
        })
        await this.loadData()
      } catch (error) {
        console.log(error)
      }
    }
  },
  async mounted () {
    await this.loadData()
  }
}
